<template>
    <main class="page">
        <span v-if="atpLiteHeaderEnabled === true">
         <top-level-menu v-if="user && canShowMenus" @hideDropDown="toggleDropDown(false)"></top-level-menu>
         <title-menu @signOut="signOut" @ready="onSaasReady" :authenticated="authenticated" :saasHeaderLoading="saasHeaderLoading" />
         <navigation-panel v-if="authenticated && (!configuration.maintenanceMode || user.internalUser) && canShowMenus"></navigation-panel>
        </span>
        <span v-else-if="atpLiteHeaderEnabled === false">
         <top-level-menu v-if="user" @hideDropDown="toggleDropDown(false)"></top-level-menu>
         <title-menu @signOut="signOut" :authenticated="authenticated" />
         <navigation-panel v-if="authenticated && (!configuration.maintenanceMode || user.internalUser)"></navigation-panel>
        </span>
        <router-view></router-view>
        <popup-message :enabled="showCookieMessage">
            <template v-slot:content>
                <h2>This website uses cookies</h2>
                <p>We use necessary cookies for authentication and session management.</p>
            </template>
            <template v-slot:controls>
                <router-link class="button button-secondary" to="/cookie-detail" @click="acceptCookies">Show details</router-link>
                <button class="button button-primary" @click="acceptCookies" >Accept</button>
            </template>
        </popup-message>
    </main>
</template>

<script>
    import { mapActions, mapGetters, mapMutations } from 'vuex'
    import TitleMenu from './components/TitleMenu.vue'
    import NavigationPanel from './components/NavigationPanel.vue'
    import TopLevelMenu from './components/TopLevelMenu.vue'
    import PopupMessage from './components/PopupMessage.vue'

    export default {
        name: 'App',
        components: {
            'top-level-menu': TopLevelMenu,
            'title-menu': TitleMenu,
            'navigation-panel': NavigationPanel,
            'popup-message': PopupMessage
        },
        data: function () {
            return {
                authenticated: false,
                showCookieMessage: true,
                saasHeaderLoading: false,
                saasHeaderReady: false
            }
        },
        async created() {
            this.showCookieMessage = !this.cookieConsent
            
            // Define logoutFunction immediately
            window.logoutFunction = async () => {
                await this.signOut?.();
            };
            
            // Start SaaS header loading IMMEDIATELY (don't wait for anything)
            this.startSaasHeaderPreload();
            
            await this.getSession()
            
            // Simplified: User data now includes feature flags!
            // No need for separate feature flag API calls
            if (await this.$auth.isAuthenticated()) {
                this.authenticated = true;
                const oktaToken = await this.$auth.getAccessToken();
                
                // Load user (which includes feature flags) and insights in parallel
                await Promise.all([
                    this.checkSession(),
                    this.getUser(oktaToken),  // ? Feature flags included in user response!
                    this.getInsights(oktaToken)
                ]);
                
                // Check maintenance mode after user loads
                if (this.user && this.configuration && !this.user.internalUser && 
                    this.configuration.maintenanceMode && this.$route.path != '/maintenance') {
                    this.$router.push('/maintenance')
                }
            } else {
                await this.isAuthenticated();
            }
        },
        watch: {
            // Everytime the route changes, check for auth status
            '$route': 'isAuthenticated'
        },
        computed: {
            ...mapGetters(['user', 'configuration', 'insights', 'cookieConsent','atpLiteHeaderEnabled']),
            
            isEiqUser() {
                return this.user && this.user.name && !this.user.name.includes('@');
            },
            
            canShowMenus() {
                // For EIQ users (no SaaS header), show immediately
                if (this.isEiqUser) {
                    return true;
                }
                // For SaaS users, wait for header to be ready
                return this.saasHeaderReady;
            }
        },
        methods: {
            ...mapActions(['getSession', 'checkSession', 'clearSession', 'getUser', 'getInsights', 'fetchAscendMarketingFlag', 'logEvent','fetchATPFlag']),
            ...mapMutations(['SET_COOKIES']),
            
            onSaasReady() {
                this.saasHeaderReady = true;
            },
            
            startSaasHeaderPreload() {
                // Start loading SaaS header script immediately in parallel
                this.saasHeaderLoading = true;
                
                fetch('/api/configuration/saas-header-script')
                    .then(response => response.json())
                    .then(data => {
                        const scriptUrl = data.script;
                        
                        // Check if script already loaded
                        if (window.ExperianSaaSComponent && window.ExperianSaaSComponent.SaasHeaderLite) {
                            this.saasHeaderLoading = false;
                            return;
                        }
                        
                        // Load script
                        const script = document.createElement('script');
                        script.type = 'text/javascript';
                        script.src = scriptUrl;
                        script.async = true;
                        script.onload = () => {
                            this.saasHeaderLoading = false;
                            window.dispatchEvent(new CustomEvent('saas-header-script-loaded'));
                        };
                        script.onerror = () => {
                            this.saasHeaderLoading = false;
                            this.logEvent('Error preloading SaaS Header script');
                        };
                        document.head.appendChild(script);
                    })
                    .catch(err => {
                        this.saasHeaderLoading = false;
                        console.error('Failed to preload SaaS header script URL:', err);
                    });
            },
            
            async isAuthenticated() {
                this.authenticated = await this.$auth.isAuthenticated()
                const session = await this.checkSession()

                if (!session.data.active) {
                    this.signOut()
                }

                if (this.authenticated) {
                    if (!this.insights) {
                        const oktaToken = await this.$auth.getAccessToken()
                        await this.getInsights(oktaToken)
                    }

                    if (!this.user) {
                        const oktaToken = await this.$auth.getAccessToken()
                        await this.getUser(oktaToken)
                    }
                }

                //newrelic.interaction().setAttribute('userName', this.user.fullName).setAttribute('companyName', this.user.company.name).save().end(); // eslint-disable-line

                if (this.user && this.configuration && !this.user.internalUser && this.configuration.maintenanceMode && this.$router.currentRoute.path != '/maintenance') {
                    this.$router.push('/maintenance')
                }
            },
            
            async signOut() {
                await this.clearSession()
                await this.$auth.signOut()
                await this.isAuthenticated()
                await this.logEvent('User signed out')
            },
            
            acceptCookies() {
                this.SET_COOKIES()
                this.showCookieMessage = false
            }
        }
    }
</script>

<style lang="scss">
    @import './styles/base';
    @import '../node_modules/hd-styles/base';
    @import '../node_modules/hd-styles/modules/mixins';

    .page {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .page-content {
        padding: $spacing-unit;
        height: 100%;
    }

    .page-content-full {
        width: 100%;
        text-align: center;
        display: block;
    }

    #app {
        height: 100%;
    }
</style>